import { useState } from 'react'
import { ModalForm, ProForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import type { inferInput } from '@trpc/tanstack-react-query'
import { Alert, App, DatePicker, Form, Input, Select } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import type { z } from 'zod'
import type { billSchema } from '@super-mega/server/contracts/bill'
import { AttachmentUpload } from '@/features/files/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { BillIssueNotice } from './bill-issue-notice.tsx'
import { useIssuedBills } from './hooks/use-issued-bills.ts'
import { MoneyAllocationFields } from './money-allocation-fields.tsx'
import type { MoneyAllocationRow } from './money-entry.types.ts'
import { OrganizationAccountSelect } from './organization-account-select.tsx'
import { fromCents, toCents } from './utils/money.ts'

type Bill = z.infer<typeof billSchema>
type Values = {
  organizationAccountId: number
  occurredAt: Dayjs
  channel: string
  name: string
  note?: string
  attachmentIds: number[]
  status: 'pending' | 'confirmed'
  rows: { amount?: string; note?: string; attachmentIds?: number[] }[]
}
export function MoneyEntry({
  bills: initialBills,
  initialAccountId,
  open = true,
  afterClose,
  onClose,
  onSaved,
  mode = 'modal',
  onRecord,
  submitting = false
}: {
  mode?: 'modal' | 'page'
  onRecord?: (input: inferInput<ReturnType<typeof useTRPC>['bill']['recordMoney']>) => Promise<void>
  submitting?: boolean
  bills: Bill[]
  initialAccountId?: number
  open?: boolean
  afterClose?: () => void
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const access = useAccess()
  const [includedIds, setIncludedIds] = useState(() =>
    initialBills
      .filter((bill) => bill.totalAmount === null || toCents(bill.outstandingAmount) > BigInt(0))
      .map((bill) => bill.id)
  )
  const selection = useIssuedBills(initialBills, 'money', includedIds)
  const { allBills: bills, ready } = selection
  const first = selection.bills[0] ?? bills[0],
    trpc = useTRPC(),
    { message } = App.useApp(),
    [key] = useState(() => crypto.randomUUID())
  const [form] = Form.useForm<Values>()
  const save = useMutation(trpc.bill.recordMoney.mutationOptions())
  const rows: MoneyAllocationRow[] = bills.map((b) => ({
    billId: b.id,
    label: `${b.billNo} · ${b.description}`,
    maximum: b.outstandingAmount
  }))
  const kind = first.direction === 'receivable' ? 'receipt' : 'payment'
  const values: Values['rows'] = Form.useWatch<Values['rows'] | undefined>('rows', form) ?? []
  const includedSet = new Set(includedIds)
  const sum = rows.reduce(
    (n, row, index) => n + (includedSet.has(row.billId) ? toCents(values[index]?.amount ?? '0') : BigInt(0)),
    BigInt(0)
  )
  const EntryForm = mode === 'page' ? ProForm<Values> : ModalForm<Values>
  const busy = save.isPending || submitting
  const paymentLabel = first.direction === 'receivable' ? '收款' : '付款'
  return (
    <EntryForm
      className={mode === 'page' ? 'flex h-full min-h-0 flex-col' : undefined}
      disabled={busy}
      dateFormatter={false}
      form={form}
      layout="vertical"
      {...(mode === 'modal'
        ? {
            open,
            onOpenChange: (nextOpen: boolean) => {
              if (!nextOpen) onClose()
            },
            title: first.direction === 'receivable' ? '登记收款' : '登记付款',
            modalProps: {
              classNames: { container: 'h-[80vh]' },
              afterClose,
              onCancel: onClose,
              destroyOnHidden: true,
              mask: { closable: false }
            }
          }
        : {})}
      initialValues={{
        organizationAccountId: initialAccountId,
        occurredAt: dayjs(),
        channel: 'bankTransfer',
        name: first.customerName,
        status: access.can(`bill.${kind}.confirm`) ? 'confirmed' : 'pending',
        attachmentIds: [],
        rows: rows.map((row) => ({ amount: toCents(row.maximum) > BigInt(0) ? row.maximum : undefined }))
      }}
      scrollToFirstError
      submitter={{
        ...(mode === 'page' ? { resetButtonProps: false as const } : {}),
        searchConfig: { submitText: `确认登记${paymentLabel}` },
        submitButtonProps: { loading: busy, disabled: !ready || selection.issuing || sum <= BigInt(0) },
        render: (_, buttons) => (
          <div
            className={
              mode === 'page'
                ? 'bg-surface border-border flex w-full shrink-0 flex-wrap items-center justify-between gap-3 border-t p-4'
                : 'flex w-full flex-wrap items-center justify-between gap-3'
            }
          >
            <div className="text-base font-semibold" aria-live="polite">
              本次{paymentLabel}合计 ¥{fromCents(sum)}{' '}
              <span className="text-sm font-normal">（{includedIds.length} 张）</span>
            </div>
            <div className="flex gap-2">{buttons}</div>
          </div>
        )
      }}
      onFinish={async (v) => {
        try {
          if (!ready) {
            return false
          }
          const allocations = rows.flatMap((r, index) => {
            const amount = v.rows[index]?.amount
            return includedSet.has(r.billId) && amount && toCents(amount) > BigInt(0)
              ? [
                  {
                    billId: r.billId,
                    amount,
                    refundOfAllocationId: r.refundOfAllocationId ?? null,
                    note: v.rows[index]?.note ?? null,
                    attachmentIds: v.rows[index]?.attachmentIds ?? []
                  }
                ]
              : []
          })
          const submittedTotal = allocations.reduce((n, a) => n + toCents(a.amount), BigInt(0))
          if (!allocations.length || allocations.length !== includedIds.length) {
            void message.error('请为本次保留的每张账单填写收付款金额，不付款的账单请移出')
            return false
          }
          const input: inferInput<ReturnType<typeof useTRPC>['bill']['recordMoney']> = {
            organizationId: first.organizationId,
            organizationAccountId: v.organizationAccountId,
            customerId: first.customerId,
            payeeId: first.payeeId,
            kind,
            status: v.status,
            amount: fromCents(submittedTotal),
            channel: v.channel,
            occurredAt: v.occurredAt.toDate(),
            counterpartySnapshot: { name: v.name },
            note: v.note ?? null,
            attachmentIds: v.attachmentIds,
            idempotencyKey: key,
            allocations
          }
          if (onRecord) {
            await onRecord(input)
          } else {
            await save.mutateAsync(input)
          }
          await onSaved()
          void message.success(v.status === 'confirmed' ? '资金已确认' : '已保存待确认流水')
          return true
        } catch (e) {
          void message.error(e instanceof Error ? e.message : '登记失败')
          return false
        }
      }}
    >
      <div className={mode === 'page' ? 'min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6' : 'flex flex-col gap-4'}>
        <BillIssueNotice {...selection} />
        <div className="bg-muted rounded-lg p-3">
          <div className="font-medium">
            {first.organizationName} · {first.customerName}
          </div>
          <p className="text-content-muted mt-1 mb-0 text-sm">
            逐张填写本次{paymentLabel}金额，未付款的账单可移出，移出后可重新加入。本次选择不会修改账单组成员。
          </p>
        </div>
        {!includedIds.length && <Alert type="info" title="请加入本次需要收付款的账单" />}
        <div>
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Form.Item name="occurredAt" label="实际发生时间" rules={[{ required: true }]}>
              <DatePicker showTime className="w-full!" />
            </Form.Item>
            <Form.Item
              name="organizationAccountId"
              label={kind === 'receipt' ? '主体收款账户' : '主体付款账户'}
              rules={[{ required: true }]}
            >
              <OrganizationAccountSelect
                purpose={first.direction === 'receivable' ? 'bill.receipt.record' : 'bill.payment.record'}
                organizationId={first.organizationId}
              />
            </Form.Item>
            <Form.Item
              name="channel"
              label={first.direction === 'receivable' ? '收款方式' : '付款方式'}
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: 'bankTransfer', label: '银行转账' },
                  { value: 'wechat', label: '微信' },
                  { value: 'alipay', label: '支付宝' },
                  { value: 'cash', label: '现金' }
                ]}
              />
            </Form.Item>
            <Form.Item name="name" label="实际对方户名" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </div>
          <MoneyAllocationFields
            rows={rows}
            refund={false}
            includedIds={includedIds}
            paymentLabel={paymentLabel}
            disabled={busy || selection.issuing}
            onToggle={(id, included) => {
              const nextIds = included ? [...includedIds, id] : includedIds.filter((value) => value !== id)
              const nextSet = new Set(nextIds)
              const nextFirst = bills.find((bill) => nextSet.has(bill.id)) ?? bills[0]
              if (nextFirst.organizationId !== first.organizationId) {
                form.setFieldValue('organizationAccountId', undefined)
              }
              if (nextFirst.customerId !== first.customerId || nextFirst.payeeId !== first.payeeId) {
                form.setFieldValue('name', nextFirst.customerName)
              }
              setIncludedIds(nextIds)
            }}
          />
          <Form.Item name="note" label="共同备注">
            <Input.TextArea maxLength={5000} />
          </Form.Item>
          <Form.Item name="attachmentIds" label="共同截图／回单">
            <AttachmentUpload purpose="moneyAttachment" maxCount={30} />
          </Form.Item>
          <Form.Item name="status" label="登记状态">
            <Select
              options={[
                { value: 'confirmed', disabled: !access.can(`bill.${kind}.confirm`), label: '确认入账' },
                { value: 'pending', label: '待确认（不计金额）' }
              ]}
            />
          </Form.Item>
        </div>
      </div>
    </EntryForm>
  )
}
